import type { EmojiClickData } from 'emoji-picker-react';
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { Props } from './types';

const EmojiPicker = lazy(() => import('emoji-picker-react'));

const PICKER_WIDTH = 300;
const VIEWPORT_MARGIN_PX = 8;

const EmojiPickerButton = ({ onEmojiClick, className, disabled }: Props) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // null = closed; {top, left} = open at that viewport position
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!pickerPos) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!buttonRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setPickerPos(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pickerPos]);

  const togglePicker = () => {
    setPickerPos((prev) => {
      if (prev) return null;
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return null;
      // Anchor to the button's right edge, but clamp so it never overflows either side.
      const maxLeft = window.innerWidth - PICKER_WIDTH - VIEWPORT_MARGIN_PX;
      const left = Math.min(Math.max(rect.right - PICKER_WIDTH, VIEWPORT_MARGIN_PX), maxLeft);
      return { top: rect.bottom + 4, left };
    });
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onEmojiClick(emojiData.emoji);
    setPickerPos(null);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={className}
        disabled={disabled}
        aria-label="Insert emoji"
        title="Insert emoji"
        onClick={togglePicker}>
        😊
      </button>
      {pickerPos &&
        createPortal(
          // position: fixed escapes any overflow-hidden ancestor
          <div
            ref={popoverRef}
            style={{ position: 'fixed', top: pickerPos.top, left: pickerPos.left, zIndex: 9999 }}>
            <Suspense fallback={null}>
              <EmojiPicker onEmojiClick={handleEmojiClick} width={PICKER_WIDTH} />
            </Suspense>
          </div>,
          document.body
        )}
    </>
  );
};
export default React.memo(EmojiPickerButton);
